// React/Preact type conflicts are resolved via type declarations in src/types/preact-react.d.ts
import * as React from "react"
import {
  Bot,
  Code,
  FolderOpen,
  Github,
  Settings2,
  User,
  History,
  Database,
  FileText,
  Edit,
  Folder,
  MessageSquare,
  CheckSquare,
  BarChart3,
} from "lucide-preact"

import { NavMain } from "@/components/nav-main"
import { NavProjects } from "@/components/nav-projects"
import { NavUser } from "@/components/nav-user"
import { TeamSwitcher } from "@/components/team-switcher"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
} from "@/components/ui/sidebar"

interface AppSidebarProps {
  user?: {
    name: string;
    email: string;
    avatar?: string;
    organization?: string;
    organizationRole?: string;
  };
  subscription?: {
    type: string;
    status: string;
    hasAvailableSubscription: boolean;
    hasOpusPlanDefault: boolean;
    recommendedSubscription: string;
    isQualifiedForDataSharing: boolean;
  };
  sessions?: Array<{
    sessionId: string;
    status?: string;
  }>;
  currentSession?: string;
  currentPage?: string;
  isAuthenticated?: boolean;
  onSessionSelect?: (sessionId: string) => void;
  onPageChange?: (page: string) => void;
  onLogin?: () => void;
  onLogout?: () => void;
  currentProject?: {
    id: string;
    name: string;
    path: string;
  } | null;
}

// Byte DevOps specific data
const getClaudeCodeData = (
  user?: AppSidebarProps['user'], 
  sessions?: AppSidebarProps['sessions'],
  currentPage?: string,
  onPageChange?: (page: string) => void,
  currentProject?: AppSidebarProps['currentProject']
) => ({
  user: user,
  teams: [
    {
      name: "Byte DevOps",
      logo: Code,
      plan: "Cloudflare",
    },
  ],
  navMain: currentProject ? [
    // Project tabs - only show when a project is selected
    {
      title: "Chat",
      url: "#",
      icon: MessageSquare,
      isActive: currentPage === 'chat',
      onClick: () => onPageChange?.('chat'),
    },
    {
      title: "Todos",
      url: "#",
      icon: CheckSquare,
      isActive: currentPage === 'todos',
      onClick: () => onPageChange?.('todos'),
    },
    {
      title: "Overview",
      url: "#",
      icon: BarChart3,
      isActive: currentPage === 'overview',
      onClick: () => onPageChange?.('overview'),
    },
    {
      title: "Editor",
      url: "#",
      icon: FileText,
      isActive: currentPage === 'files',
      onClick: () => onPageChange?.('files'),
    },
    {
      title: "Sessions",
      url: "#",
      icon: History,
      isActive: currentPage === 'sessions',
      onClick: () => onPageChange?.('sessions'),
    },
    {
      title: "Agents",
      url: "#",
      icon: Bot,
      isActive: currentPage === 'agents',
      onClick: () => onPageChange?.('agents'),
    },
    {
      title: "Settings",
      url: "#",
      icon: Settings2,
      isActive: currentPage === 'settings',
      onClick: () => onPageChange?.('settings'),
    },
    {
      title: "Back to Projects",
      url: "#",
      icon: Folder,
      isActive: false,
      onClick: () => onPageChange?.('projects'),
    },
  ] : [
    // Project selection - only show when no project is selected
    {
      title: "Projects",
      url: "#",
      icon: FolderOpen,
      isActive: true,
      onClick: () => onPageChange?.('projects'),
      items: [
        {
          title: "Create Project",
          url: "#",
          onClick: () => onPageChange?.('projects'),
        },
        {
          title: "Import Project",
          url: "#",
          onClick: () => onPageChange?.('projects'),
        },
      ],
    },
  ],
  projects: sessions?.map(session => ({
    name: session.sessionId,
    url: "#",
    icon: FolderOpen,
    id: session.sessionId,
    status: session.status || 'active',
    onClick: () => onPageChange?.('terminal'),
  })) || [],
})

export function AppSidebar({
  user,
  subscription,
  sessions,
  currentSession,
  currentPage,
  isAuthenticated,
  onSessionSelect,
  onPageChange,
  onLogin,
  onLogout,
  currentProject,
  ...props
}: AppSidebarProps) {
  const data = getClaudeCodeData(user, sessions, currentPage, onPageChange, currentProject)
  
  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <TeamSwitcher teams={data.teams} />
      </SidebarHeader>
      <SidebarContent>
        <NavMain items={data.navMain} />
        <NavProjects 
          projects={data.projects} 
          currentProject={currentSession}
          onProjectSelect={onSessionSelect}
        />
      </SidebarContent>
      <SidebarFooter>
        <NavUser 
          user={data.user} 
          subscription={subscription}
          isAuthenticated={isAuthenticated}
          onLogin={onLogin}
          onLogout={onLogout}
        />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}